import type { SortDescriptor } from "react-stately";

import { RequestTypeEnum } from "@nlc/shared-types";
import { memo, useMemo, useState } from "react";

import { Link } from "@/components/link";
import { allTableConfig } from "@/features/requests/config/tables";
import { useWhitelabel } from "@/hooks/useWhitelabel";

import type { RequestFilterType } from "../../../types/filters";

import { useAllRequests } from "../../../hooks";
import { useResetFilterOnEmptySearch } from "../../../hooks/shared";
import { ConfigurableTable } from "../../tables";

interface AllTabProps {
  onFilterChange?: (filter: RequestFilterType) => void;
  searchValue?: string;
  selectedFilter?: RequestFilterType;
}

function AllTabComponent({
  onFilterChange,
  searchValue,
  selectedFilter,
}: AllTabProps) {
  const { isWhitelabel } = useWhitelabel();
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: allTableConfig.defaultSort?.column || "createdAt",
    direction:
      allTableConfig.defaultSort?.direction === "desc"
        ? "descending"
        : "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const {
    data: requestsData,
    isFetching,
    isLoading,
    isSuccess,
  } = useAllRequests({
    filter: selectedFilter,
    limit: itemsPerPage,
    page: currentPage,
    search: searchValue,
    sortDescriptor,
  });

  useResetFilterOnEmptySearch({
    isAlreadyBroadest: !selectedFilter || selectedFilter === "all",
    isReady: isSuccess && !isFetching,
    onSwitchToAll: () => onFilterChange?.("all"),
    searchTerm: searchValue,
    totalMatches: requestsData?.total ?? 0,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const allTabConfigWithTemplateCell = useMemo(
    () => ({
      ...allTableConfig,
      columns: allTableConfig.columns.map((col) =>
        col.key === "name"
          ? {
              ...col,
              cellRenderer: (
                value: unknown,
                row: {
                  dueDate?: string;
                  id: number;
                  isTemplate?: boolean;
                  requestType?: RequestTypeEnum;
                },
              ) => {
                const name =
                  (typeof value === "string" ? value : "") || "Untitled";

                const href =
                  row.isTemplate && row.requestType
                    ? `/requests/template?request-type=${String(row.requestType).toLowerCase()}`
                    : `/requests/${row.id}/edit`;

                return (
                  <div className="max-w-full overflow-hidden">
                    <Link
                      className="font-medium text-text-primary hover:no-underline cursor-pointer line-clamp-3 break-words block"
                      href={href}
                    >
                      {name}
                    </Link>
                  </div>
                );
              },
            }
          : col,
      ),
    }),
    [],
  );

  const allRequests = requestsData?.requests || [];

  const templateRows = useMemo(() => {
    if (isWhitelabel) return [];

    const nowIso = new Date().toISOString();
    const dueInTwoDaysIso = new Date(
      Date.now() + 2 * 24 * 60 * 60 * 1000,
    ).toISOString();

    const demoBrand = { id: 0, name: "Demo brand" } as const;
    const demoCreator = { id: 0, name: "Demo user" } as const;
    const demoAssignee = { id: 0, name: "Jason G" } as const;
    const demoStatus = { id: 3, name: "Ready for review" } as const;

    return [
      {
        assignee: demoAssignee,
        brand: demoBrand,
        createdAt: nowIso,
        createdBy: demoCreator,
        designType: "Carousel Ads",
        dueDate: dueInTwoDaysIso,
        id: -1,
        isTemplate: true,
        name: "Graphic Request template",
        projectId: 0,
        requestType: RequestTypeEnum.GRAPHIC,
        status: demoStatus,
      },
      {
        assignee: demoAssignee,
        brand: demoBrand,
        createdAt: nowIso,
        createdBy: demoCreator,
        designType: "A+/EBC",
        dueDate: dueInTwoDaysIso,
        id: -2,
        isTemplate: true,
        name: "Video Request template",
        projectId: 0,
        requestType: RequestTypeEnum.VIDEO,
        status: demoStatus,
      },
    ];
  }, []);

  const dataWithTemplates = useMemo(
    () => (currentPage === 1 ? [...allRequests, ...templateRows] : allRequests),
    [allRequests, templateRows, currentPage],
  );

  return (
    <ConfigurableTable
      config={allTabConfigWithTemplateCell}
      data={dataWithTemplates}
      isLoading={isLoading}
      paginationProps={{
        metadata: {
          currentPage,
          itemsPerPage,
          totalItems: requestsData?.total ?? null,
          totalPages: requestsData?.pagination?.totalPages ?? null,
        },
        onItemsPerPageChange: handleItemsPerPageChange,
        onPageChange: handlePageChange,
        pageSizeOptions: [10, 25, 50, 100],
        showPageSizeSelector: true,
      }}
      setSortDescriptor={setSortDescriptor}
      sortDescriptor={sortDescriptor}
      useAdvancedSorting={true}
    />
  );
}

export const AllTab = memo(AllTabComponent);
